(() => {
  if (window.micLockChatClearanceLoaded) return;
  window.micLockChatClearanceLoaded = true;

  const hiddenClass = 'ml-chat-clearance-hidden';
  const activeClass = 'ml-chat-inline-active';
  const overlays = '#chat-button, #mount-floating-engagement-widget, #chat-campaigns, #chat-input';
  const controls = '.product-form__buttons, variant-selects, .product-form__quantity, .quick-add__submit, .cart__ctas, .cart__dynamic-checkout-buttons, .cart-item__quantity-wrapper, .cart__note, .ml-connector-guide';
  const dialogs = 'quick-add-modal[open], cart-drawer.active, details-modal.header__search details[open]';
  const secondaryControls = '.mic-lock-contact .form-status, .mic-lock-contact .form-status-list, .mic-lock-contact input, .mic-lock-contact textarea, .mic-lock-contact button, .mic-lock-contact p, .mic-lock-contact a, .search-device-container select, .search-device-container button, .footer a, .footer button, .footer input, .footer select, .footer .disclosure__list-wrapper, .footer p, .footer h2, .footer .list-payment, .footer .copyright__content';
  const relocationObstacles = '.header-wrapper, .utility-bar, #MainContent .card-wrapper, #MainContent a, #MainContent button, #MainContent input, #MainContent select, #MainContent summary, #MainContent img, #MainContent h1, #MainContent h2, #MainContent h3, #MainContent p';
  let nativePosition, appliedPosition, positionApi, positionFailures = 0, positionRetryAt = 0, positionMoveAt = 0, unsettledMoves = 0;
  const inlineButtons = new Map();
  const observed = new Set();
  let api, activeAttempt, scheduled = false, frameId, retryTimer, paused = false, lastTrigger, returnFocus;
  const attempts = new WeakMap();
  const subscriptions = new WeakMap();
  let listeners = [];
  const attributeTargets = new Set();
  const relevant = `${overlays}, #chat-window, product-form, ${controls}, ${secondaryControls}, quick-add-modal, cart-drawer, details-modal.header__search, .header-wrapper, .utility-bar`;

  function schedule() {
    if (scheduled || paused) return;
    scheduled = true;
    frameId = requestAnimationFrame(() => {
      scheduled = false;
      update();
    });
  }

  function visibleRect(element, ignoreOwnVisibility = false) {
    if (!element || !element.getClientRects().length) return null;
    const style = getComputedStyle(element);
    if (style.display === 'none' || (!ignoreOwnVisibility && style.visibility === 'hidden')) return null;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 ? rect : null;
  }

  function viewportContains(rect) {
    const view = window.visualViewport;
    const left = view ? view.offsetLeft : 0;
    const top = view ? view.offsetTop : 0;
    return rect.right > left && rect.bottom > top &&
      rect.left < left + (view ? view.width : window.innerWidth) &&
      rect.top < top + (view ? view.height : window.innerHeight);
  }

  function intersects(a, b, gap) {
    return a.left < b.right + gap && a.right > b.left - gap &&
      a.top < b.bottom + gap && a.bottom > b.top - gap;
  }

  function restore() {
    document.querySelectorAll(overlays).forEach(frame => {
      if (frame.classList.contains(hiddenClass)) frame.classList.remove(hiddenClass);
    });
  }

  function setInline(button, active) {
    if (button.classList.contains(activeClass) !== active) button.classList.toggle(activeClass, active);
    if (button.inert !== !active) button.inert = !active;
    if (button.tabIndex !== (active ? 0 : -1)) button.tabIndex = active ? 0 : -1;
    if (button.getAttribute('aria-hidden') !== String(!active)) button.setAttribute('aria-hidden', String(!active));
  }

  function activateInline(selected) {
    inlineButtons.forEach(button => setInline(button, button === selected));
  }

  function focusedInline() {
    return [...inlineButtons.values()].find(button => document.activeElement === button);
  }

  function fallbackButtons() {
    return [...inlineButtons.entries()].filter(([form, button]) => {
      const formStyle = getComputedStyle(form);
      if (!form.isConnected || form.hidden || form.hasAttribute('inert') || form.inert || formStyle.display === 'none' || formStyle.visibility === 'hidden') return false;
      if (!button.isConnected || !visibleRect(button, true)) return false;
      for (let parent = button.parentElement; parent; parent = parent.parentElement) {
        const style = getComputedStyle(parent);
        if (parent.hidden || parent.hasAttribute('inert') || parent.inert || style.display === 'none' || style.visibility === 'hidden') return false;
      }
      return true;
    }).map(([, button]) => button);
  }

  function chooseFallback(candidates) {
    const focused = focusedInline();
    if (candidates.includes(focused)) return focused;
    if (candidates.includes(returnFocus)) return returnFocus;
    return candidates.find(button => viewportContains(button.getBoundingClientRect())) || candidates[0];
  }

  function releaseInlineFocus(target) {
    const focused = focusedInline();
    if (!focused) return;
    if (target && visibleRect(target)) target.focus({ preventScroll: true });
    if (document.activeElement === focused) focused.blur();
  }

  function clearWork() {
    window.clearTimeout(retryTimer);
    retryTimer = undefined;
    cancelAnimationFrame(frameId);
    scheduled = false;
    resizeObserver?.disconnect();
    observed.clear();
    attributeObserver.disconnect();
    attributeTargets.clear();
    listeners.forEach(([instance, name, callback]) => {
      try {
        if (typeof instance.off === 'function') {
          instance.off(name, callback);
          subscriptions.get(instance)?.delete(name);
        }
      } catch (_) {}
    });
    listeners = [];
    nativePosition = appliedPosition = positionApi = undefined;
    positionFailures = positionRetryAt = positionMoveAt = unsettledMoves = 0;
  }

  function deactivate() {
    clearWork();
    api = undefined;
    restore();
    releaseInlineFocus(document.querySelector('#chat-button'));
    activateInline();
    lastTrigger = returnFocus = undefined;
  }

  function unavailable(instance) {
    if (instance !== api && instance !== activeAttempt?.source) return;
    const record = attempts.get(instance);
    if (record) record.status = 'failed';
    activeAttempt = undefined;
    deactivate();
  }

  function retryPosition(delay) {
    window.clearTimeout(retryTimer);
    const current = api;
    retryTimer = window.setTimeout(() => {
      retryTimer = undefined;
      if (api === current) schedule();
    }, delay);
  }

  function makeInlineButton(form) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ml-chat-inline';
    setInline(button, false);
    button.setAttribute('aria-label', 'Chat with us');
    button.title = 'Chat with us';
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const path = document.createElementNS(svg.namespaceURI, 'path');
    path.setAttribute('d', 'M12 3a9 9 0 0 0-7.8 13.5L3 21l4.5-1.2A9 9 0 1 0 12 3Z');
    svg.append(path);
    for (const x of [8, 12, 16]) {
      const dot = document.createElementNS(svg.namespaceURI, 'circle');
      dot.setAttribute('cx', x);
      dot.setAttribute('cy', '12');
      dot.setAttribute('r', '1');
      dot.setAttribute('fill', '#0086ff');
      svg.append(dot);
    }
    button.append(svg);
    button.addEventListener('click', () => {
      if (!api || !button.classList.contains(activeClass) || button.inert) return;
      const current = api;
      lastTrigger = button;
      try {
        Promise.resolve(current.open()).catch(() => unavailable(current));
        schedule();
      } catch (_) {
        unavailable(current);
      }
    });
    form.after(button);
    inlineButtons.set(form, button);
  }

  function refreshInlineButtons() {
    inlineButtons.forEach((button, form) => {
      if (!form.isConnected || !button.isConnected) {
        if (lastTrigger === button) lastTrigger = undefined;
        if (returnFocus === button) returnFocus = undefined;
        button.remove();
        inlineButtons.delete(form);
      }
    });
    if (!api) return;
    document.querySelectorAll('product-form').forEach(form => {
      if (form.closest('quick-add-modal, cart-drawer') || !form.querySelector('.product-form__buttons') || inlineButtons.has(form)) return;
      makeInlineButton(form);
    });
  }

  const resizeObserver = window.ResizeObserver ? new ResizeObserver(schedule) : null;

  function observeSizes(elements) {
    if (!resizeObserver) return;
    const current = new Set(elements);
    // Container size changes can move a control without resizing the control.
    // Observe those ancestors instead of every animated descendant.
    elements.forEach(element => {
      for (let parent = element.parentElement; parent; parent = parent.parentElement) current.add(parent);
    });
    observed.forEach(element => {
      if (!current.has(element)) {
        resizeObserver.unobserve(element);
        observed.delete(element);
      }
    });
    current.forEach(element => {
      if (!observed.has(element)) {
        resizeObserver.observe(element);
        observed.add(element);
      }
    });
  }

  // Gorgias owns the launcher. Its documented API changes only this page's
  // widget position; it does not save the shared Appearance settings.
  function clearSecondaryControls(native, secondaryRects, purchaseRects, modalOpen) {
    if (!native || !api || typeof api.setPosition !== 'function') return;
    if (positionApi !== api) {
      positionApi = api;
      nativePosition = appliedPosition = undefined;
      positionFailures = positionRetryAt = positionMoveAt = unsettledMoves = 0;
    }
    if (positionFailures >= 3 || Date.now() < positionRetryAt || modalOpen || focusedInline() ||
      document.activeElement?.closest('#gorgias-chat-container')) return;
    const actual = visibleRect(native, native.classList.contains(hiddenClass));
    if (!actual) return;
    if (!nativePosition) {
      const vertical = native.style.bottom ? 'bottom' : native.style.top ? 'top' : undefined;
      const horizontal = native.style.left ? 'left' : native.style.right ? 'right' : undefined;
      if (!vertical || !horizontal) return;
      const offsetX = Number.parseFloat(native.style[horizontal]) - 20;
      const offsetY = Number.parseFloat(native.style[vertical]) - 20;
      if (![offsetX, offsetY].every(value => Number.isInteger(value) && value >= -20 && value <= 200)) return;
      nativePosition = { alignment: `${vertical}-${horizontal}`, offsetX, offsetY };
      appliedPosition = nativePosition;
    }
    const view = window.visualViewport;
    const viewport = {
      left: view ? view.offsetLeft : 0, top: view ? view.offsetTop : 0,
      right: (view ? view.offsetLeft + view.width : window.innerWidth),
      bottom: (view ? view.offsetTop + view.height : window.innerHeight)
    };
    const rectAt = position => {
      const left = position.alignment.endsWith('left') ? 20 + position.offsetX : window.innerWidth - 20 - position.offsetX - actual.width;
      const top = position.alignment.startsWith('top') ? 20 + position.offsetY : window.innerHeight - 20 - position.offsetY - actual.height;
      return { left, top, right: left + actual.width, bottom: top + actual.height };
    };
    // A completed previous move clears the retry budget even if scrolling
    // now requires a different destination.
    const appliedRect = rectAt(appliedPosition);
    if (['left', 'top', 'right', 'bottom'].every(edge => Math.abs(actual[edge] - appliedRect[edge]) < 2)) unsettledMoves = 0;
    const baseRect = rectAt(nativePosition);
    const gap = appliedPosition === nativePosition ? 12 : 24;
    let target = nativePosition;
    // The established purchase fallback retains priority on product/cart pages.
    if (!purchaseRects.some(rect => intersects(baseRect, rect, 24)) &&
      secondaryRects.some(rect => intersects(baseRect, rect, gap))) {
      const contentRects = [...document.querySelectorAll(relocationObstacles)]
        .map(element => visibleRect(element)).filter(rect => rect && viewportContains(rect));
      const blocked = [...secondaryRects, ...purchaseRects, ...contentRects];
      const clear = position => {
        const rect = rectAt(position);
        return rect.left >= viewport.left + 8 && rect.right <= viewport.right - 8 &&
          rect.top >= viewport.top + 8 && rect.bottom <= viewport.bottom - 8 &&
          !blocked.some(control => intersects(rect, control, 12)) &&
          !purchaseRects.some(control => intersects(rect, control, 24));
      };
      if (appliedPosition !== nativePosition && clear(appliedPosition)) target = appliedPosition;
      else {
        const offsets = [...new Set([nativePosition.offsetY, 0, 25, 50, 75, 100, 150, 175, 200])];
        const candidates = ['bottom-left', 'bottom-right', 'top-left', 'top-right']
          .flatMap(alignment => offsets.map(offsetY => ({ alignment, offsetX: nativePosition.offsetX, offsetY })));
        const distance = position => {
          const rect = rectAt(position);
          return Math.abs(rect.top - baseRect.top) + Math.abs(rect.left - baseRect.left);
        };
        candidates.sort((a, b) => distance(a) - distance(b));
        target = candidates.find(clear);
        // A tightly packed phone view can leave no permitted Gorgias corner.
        // Temporarily suppress closed chat instead of covering a different link;
        // scrolling, resizing or opening chat resumes the normal evaluation.
        if (!target) return true;
      }
    }
    const expected = rectAt(target);
    const geometryMatches = ['left', 'top', 'right', 'bottom'].every(edge => Math.abs(actual[edge] - expected[edge]) < 2);
    const targetUnchanged = target.alignment === appliedPosition.alignment && target.offsetX === appliedPosition.offsetX && target.offsetY === appliedPosition.offsetY;
    if (geometryMatches) unsettledMoves = 0;
    if (targetUnchanged && geometryMatches) return;
    if (unsettledMoves >= 3) return;
    // React may not have rendered the last native position yet. Recheck once
    // after it settles; do not dispatch repeatedly during that transition.
    if (Date.now() - positionMoveAt < 250) return !geometryMatches;
    try {
      api.setPosition(target);
      unsettledMoves++;
      appliedPosition = target;
      positionMoveAt = Date.now();
      positionFailures = positionRetryAt = 0;
      retryPosition(275);
      // Do not expose the old hitbox while Gorgias renders the new position.
      return !geometryMatches;
    } catch (_) {
      // A transient API failure gets bounded retries. Native support stays
      // available, and a broken API cannot cause a scroll-time retry loop.
      positionFailures++;
      positionRetryAt = Date.now() + 1000;
      if (positionFailures < 3) retryPosition(1050);
    }
  }

  function update() {
    initialize();
    refreshInlineButtons();
    if (!api) return;
    const frames = [...document.querySelectorAll(overlays)];
    const purchaseControls = [...document.querySelectorAll(controls)];
    const extraControls = [...document.querySelectorAll(secondaryControls)];
    observeSizes([...frames, ...purchaseControls, ...extraControls]);
    observeAttributes([...document.querySelectorAll(relevant)]);
    const chatWindow = document.querySelector('#chat-window');
    const windowVisible = !!visibleRect(chatWindow);
    let open;
    try {
      open = api.isOpen() || windowVisible;
    } catch (_) {
      return unavailable(api);
    }
    if (open) {
      // The API can report open before its iframe is ready to receive focus.
      // Keep the focused trigger usable during that short transition.
      if (focusedInline() && !windowVisible) return;
      releaseInlineFocus(chatWindow);
      activateInline();
      return restore();
    }
    const modalOpen = !!document.querySelector(dialogs);
    const protectedRects = purchaseControls.map(element => visibleRect(element)).filter(rect => rect && viewportContains(rect));
    const secondaryRects = extraControls.map(element => visibleRect(element)).filter(rect => rect && viewportContains(rect));
    const noClearPosition = clearSecondaryControls(frames.find(frame => frame.id === 'chat-button'), secondaryRects, protectedRects, modalOpen);
    const shouldHide = frame => {
      const wasHidden = frame.classList.contains(hiddenClass);
      const rect = visibleRect(frame, wasHidden);
      const protectedAreas = frame.id === 'chat-button' ? protectedRects : [...protectedRects, ...secondaryRects];
      return !!rect && (modalOpen || noClearPosition || protectedAreas.some(control => intersects(rect, control, wasHidden ? 24 : 12)));
    };
    const native = frames.find(frame => frame.id === 'chat-button');
    const nativeAvailable = !!native && !!visibleRect(native, native.classList.contains(hiddenClass));
    const supportInputFocused = frames.some(frame => frame !== native && document.activeElement === frame);
    const needsFallback = !modalOpen && !supportInputFocused &&
      (!nativeAvailable || shouldHide(native) || focusedInline() || returnFocus);
    const replacement = needsFallback ? chooseFallback(fallbackButtons()) : undefined;
    const visibility = frames.map(frame => {
      const wasHidden = frame.classList.contains(hiddenClass);
      const focused = document.activeElement === frame;
      const hidden = (!focused || frame === native) &&
        ((!!replacement && !!visibleRect(frame, wasHidden)) || shouldHide(frame));
      return { frame, wasHidden, focused, hidden };
    });
    // Activate the target before handing focus to it. The inactive slot stays in
    // normal flow so switching between native and fallback cannot move content.
    if (!replacement) releaseInlineFocus(modalOpen ? undefined : native);
    activateInline(replacement);
    if (returnFocus) {
      if (replacement === returnFocus) replacement.focus({ preventScroll: true });
      returnFocus = undefined;
    }
    visibility.forEach(({ frame, wasHidden, focused, hidden }) => {
      if (hidden && focused) {
        if (replacement && !modalOpen) replacement.focus({ preventScroll: true });
        else frame.blur();
      }
      if (wasHidden !== hidden) frame.classList.toggle(hiddenClass, hidden);
    });
  }

  function attach(instance) {
    api = instance;
    if (typeof instance.on === 'function') {
      const handlers = {
        'widget:opened': () => { if (api === instance && !paused) schedule(); },
        'widget:closed': () => {
          if (api !== instance || paused) return;
          if (lastTrigger?.isConnected &&
            (document.activeElement === document.body || document.activeElement?.closest('#gorgias-chat-container'))) {
            returnFocus = lastTrigger;
          }
          lastTrigger = undefined;
          schedule();
        },
        destroy: () => unavailable(instance)
      };
      let registered = subscriptions.get(instance);
      if (!registered) subscriptions.set(instance, registered = new Map());
      Object.entries(handlers).forEach(([name, callback]) => {
        // Some widget versions expose on but no off. Retain that subscription
        // once and gate its callback on the active instance across page restore.
        if (!registered.has(name)) {
          instance.on(name, callback);
          registered.set(name, callback);
        }
        listeners.push([instance, name, registered.get(name)]);
      });
    }
    schedule();
  }

  function initialize(retry = false, replacements = 0) {
    if (paused) return;
    const next = window.GorgiasChat;
    if (api === next && api) return;
    if (api) deactivate();
    if (activeAttempt?.source === next && next) return;
    activeAttempt = undefined;
    if (!next || (typeof next !== 'object' && typeof next !== 'function')) return;
    let record = attempts.get(next);
    if (record?.status === 'ready') {
      activeAttempt = undefined;
      try { attach(record.ready); } catch (_) { unavailable(record.ready); }
      return;
    }
    if (record?.status === 'pending') {
      activeAttempt = record.attempt;
      return;
    }
    // No polling or mutation can retry a failed bootstrap. A loaded event may
    // request one later attempt; each instance has a lifetime budget of two.
    if (record && (!retry || record.count >= 2)) return;
    record = { count: (record?.count || 0) + 1, status: 'pending' };
    attempts.set(next, record);
    const attempt = { source: next, record };
    record.attempt = attempt;
    activeAttempt = attempt;
    const failed = () => {
      record.status = 'failed';
      if (activeAttempt === attempt) unavailable(next);
      else if (api && attempts.get(api) === record) unavailable(api);
    };
    try {
      // The bootstrap Proxy permits init only. Do not inspect operational API
      // methods until this promise completes, then reacquire the installed API.
      record.promise = Promise.resolve(next.init());
      record.promise.then(() => {
        if (activeAttempt !== attempt) {
          record.status = 'failed';
          return;
        }
        const ready = window.GorgiasChat;
        if (ready !== next) {
          // A runtime replacement and a fresh bootstrap have the same public
          // surface here. Await the replacement's own init before any guarded
          // reads, sharing its existing attempt if another caller selected it.
          record.status = 'failed';
          activeAttempt = undefined;
          // Bound a broken vendor that replaces itself on every completion.
          if (replacements < 2) initialize(false, replacements + 1);
          return;
        }
        if (!ready || typeof ready.open !== 'function' || typeof ready.isOpen !== 'function') throw new Error('Chat API unavailable');
        record.status = 'ready';
        record.ready = ready;
        attempts.set(ready, record);
        activeAttempt = undefined;
        if (!paused) attach(ready);
      }).catch(failed);
    } catch (_) {
      failed();
    }
  }

  function ownClassOnly(record) {
    if (record.attributeName !== 'class') return false;
    const withoutHelper = value => (value || '').split(/\s+/).filter(name => name && name !== hiddenClass).sort().join(' ');
    return withoutHelper(record.oldValue) === withoutHelper(record.target.getAttribute('class'));
  }

  const attributeObserver = new MutationObserver(records => {
    if (records.some(record => !ownClassOnly(record))) schedule();
  });

  function observeAttributes(elements) {
    const current = new Set();
    elements.forEach(element => {
      for (let node = element; node; node = node.parentElement) current.add(node);
    });
    if (current.size === attributeTargets.size && [...current].every(node => attributeTargets.has(node))) return;
    attributeObserver.disconnect();
    attributeTargets.clear();
    current.forEach(node => {
      attributeObserver.observe(node, { attributes: true, attributeOldValue: true,
        attributeFilter: ['class', 'style', 'open', 'hidden', 'inert'] });
      attributeTargets.add(node);
    });
  }

  function relevantNode(node) {
    return node.nodeType === 1 && !node.matches('.ml-chat-inline') &&
      (node.matches(relevant) || !!node.querySelector(relevant));
  }

  // Discover widget/section insertion without subscribing to every descendant's
  // animated class/style changes. Attribute observation is scoped separately.
  const discoveryObserver = new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(relevantNode) ||
      record.target.nodeType === 1 && !record.target.closest('.ml-chat-inline') && record.target.matches(controls + ', ' + secondaryControls))) schedule();
  });

  function loaded() { initialize(true); schedule(); }
  function legacyResourceLoaded(event) {
    const target = event.target;
    if (target?.nodeType !== 1 || !target.matches('img, iframe, video, object')) return;
    if (target.matches(`${overlays}, #chat-window`) ||
      target.closest('#MainContent, product-form, cart-drawer, quick-add-modal, details-modal, .footer, .mic-lock-contact, .search-device-container, .header-wrapper, .utility-bar, #gorgias-chat-container')) schedule();
  }
  const events = [
    [window, 'gorgias-widget-loaded', loaded],
    [document, 'scroll', schedule, { capture: true, passive: true }],
    [window, 'resize', schedule, { passive: true }],
    [document, 'focusin', schedule], [document, 'focusout', schedule],
    [document, 'toggle', schedule, true], [window, 'load', schedule, { once: true }],
    [window.visualViewport, 'resize', schedule, { passive: true }],
    [window.visualViewport, 'scroll', schedule, { passive: true }]
  ];
  // ResizeObserver covers resource-driven shifts in managed regions. Capturing
  // every resource load otherwise repeats full clearance passes for unrelated
  // scripts and frames; retain only relevant media loads for older browsers.
  if (!resizeObserver) events.push([document, 'load', legacyResourceLoaded, true]);
  function resume() {
    paused = false;
    events.forEach(([target, name, callback, options]) => target?.addEventListener(name, callback, options));
    discoveryObserver.observe(document.body, { childList: true, subtree: true });
    schedule();
  }
  window.addEventListener('pagehide', () => {
    paused = true;
    events.forEach(([target, name, callback, options]) => target?.removeEventListener(name, callback, options));
    discoveryObserver.disconnect();
    deactivate();
    inlineButtons.forEach(button => button.remove());
    inlineButtons.clear();
  });
  window.addEventListener('pageshow', resume);
  resume();
})();
